import style from "../../../../assets/scss/common/modal/addConsultationModal.module.scss";
import CloseBtn from "../../../../assets/images/modal/close_btn.svg";
import React, {useEffect, useState} from "react";
import {ConsultantReviewPostDTO, ConsultationItem} from "../../../../types/consultation";
import {useConsultationGetter} from "../../../sub/consultation/hooks/useConsultationGetter";
import {useConsultationFunctions} from "../../../sub/consultation/hooks/useConsultationFunctions";
import fullStar from "../../../../assets/images/mypage/fullstar.svg";
import emptyStar from "../../../../assets/images/mypage/emptystar.svg";
import {ValidateStatus} from "../login/signup/hooks/useSignupValidator";
import {useModal} from "../../../../hooks/hooks";
import {useModalScrollLock} from "./hooks/useModalScrollLock";
import {useTranslation} from "react-i18next";

interface Args {
    isOpen: boolean;
    onClose: () => void;
    selectedConsultation: ConsultationItem | null;
    onRefresh: (_: void) => void
}
export function ReviewConsultantModal(
    {isOpen, onClose, selectedConsultation, onRefresh}: Args) {
    const { t } = useTranslation("consultant");
    useModalScrollLock(isOpen); // 모달 밖 스크롤 제한

    const {getConsultationInfo, consultationData, postConsultationReview} = useConsultationGetter();
    const {openConfirmModal, openAlertModal} = useModal();
    const {getConsultationStatus} = useConsultationFunctions();
    const [review, setReview] = useState(""); // 상담 리뷰 상태 관리
    const [rating, setRating] = useState(0); // 별점 상태 관리 (0 ~ 5)
    const [reviewValidator, setReviewValidator] = useState<ValidateStatus>(ValidateStatus.NONE)
    const [reviewRateValidator, setReviewRateValidator] = useState<ValidateStatus>(ValidateStatus.NONE)

    // 별점 클릭 핸들러
    const handleStarClick = (index: number) => {
        setRating(index + 1); // 클릭된 별 인덱스에 1을 더해서 설정
        setReviewRateValidator(ValidateStatus.NONE)
    };

    useEffect(() => {
        if (isOpen && selectedConsultation?.counselingIdx !== undefined) {
            getConsultationInfo(selectedConsultation.counselingIdx).then();
        }
        // eslint-disable-next-line
    }, [isOpen, getConsultationInfo]);

    useEffect(() => {
        if (consultationData) {
            setReview(consultationData.review ?? '');
            setRating(consultationData.reviewCount ?? 0)
        }
        // eslint-disable-next-line
    }, [consultationData]);

    if (!isOpen) {
        return null;
    }

    const handleReviewSubmit = () => {
        // 유효성 검증
        if (!selectedConsultation) return false;
        if (rating === 0) {
            setReviewRateValidator(ValidateStatus.UNFILLED)
            return false;
        }
        if (review.length < 2 || review.length > 1000) {
            setReviewValidator(ValidateStatus.BELOW_REQUIRED_LENGTH)
            return false;
        }
        const body: ConsultantReviewPostDTO = {
            review: review,
            reviewStar: rating
        };
        openConfirmModal(
            {
                title: t('modal.review.title'),
                desc: `${t('modal.review.desc1')}\n${t('modal.review.desc2')}`,
            }, () => {
                postConsultationReview({body: body, counselingIdx: selectedConsultation.counselingIdx})
                    .then(() => {
                        openAlertModal({
                            title: t('modal.review.title'),
                            desc: t('modal.review.complete'),
                            callback: () => {
                                onClose();
                                onRefresh();
                            },
                        })
                    })

            });
    }

    return (
        <div className={style.addChildModalOverlay}>
            <div className={style.addChildInfoModal}>
                <div className={style.closeBtnContainer}>
                    <img
                        src={CloseBtn}
                        alt="closeBtn"
                        className={style.closeBtn}
                        onClick={() => onClose()}
                    />
                </div>
                {selectedConsultation && consultationData && (
                    <div className={style.innerModalContainer}>
                        <h3 className={style.addChildInfoTitle}>{t('detail.modal.reviews')}</h3>
                        <div className={style.addChildInfoDesc}>
                            <span className={style.boldDesc}>{t('detail.modal.desc')}</span>
                        </div>
                        <div className={style.innerTextContainer}>
                            <div className={style.infoContainer}>
                                <span className={style.infoTitleText}>{t('detail.modal.profileInfo')}</span>
                                <span className={style.infoText}>{t('detail.modal.name')}</span>
                                <input
                                    onChange={() =>{}}
                                    placeholder={selectedConsultation.userName ? selectedConsultation.userName : ""}
                                    className={style.inputField}
                                    value={selectedConsultation.userName ? selectedConsultation.userName : ""}
                                    disabled
                                />
                                <span className={style.infoText}>{t('detail.modal.testType')}</span>
                                <input
                                    onChange={() =>{}}
                                    placeholder={t('detail.modal.testRoundPlaceholder')}
                                    className={style.inputField}
                                    value={`${selectedConsultation.unitVoucherType === "KPASS" ? "K-PASS" : "D-CAS"} ${
                                        selectedConsultation.testRound
                                    }${t('detail.modal.testRound')}`}
                                    disabled
                                />
                            </div>
                        </div>
                        <div className={style.innerTextContainer}>
                            <div className={style.infoContainer}>
                                <span className={style.infoTitleText}>{t('detail.modal.info.title')}</span>
                                <div className={style.consultInputContainer}>
                                    <span className={style.consultInputTitle}>{t('detail.modal.wishDate')}</span>
                                    <div className={style.customDateInput}>{selectedConsultation.desiredDate}</div>
                                </div>
                                <div className={style.consultInputContainer}>
                                    <span className={style.consultInputTitle}>{t('detail.modal.wishTime')}</span>
                                    <div className={style.customDateInput}>{selectedConsultation.desiredTime?.slice(0, 5)}</div>
                                </div>
                            </div>
                        </div>
                        <div className={style.innerTextContainer}>
                            <div className={style.infoContainer}>
                                <span className={style.infoTitleText}>{t('detail.modal.preQuestion.title')}</span>
                                <textarea
                                    onChange={() =>{}}
                                    placeholder={t('detail.modal.preQuestion.placeholder')}
                                    className={style.textareaInput}
                                    value={consultationData?.preQuestioning}
                                />
                            </div>
                        </div>
                        <div className={style.innerTextContainer}>
                            <div className={style.infoContainer}>
                                <span className={style.infoTitleText}>{t('detail.modal.status.title')}</span>
                                <div className={style.marginDiv}>
                                    {getConsultationStatus(consultationData.status)}
                                </div>
                            </div>
                        </div>
                        <div className={style.innerTextContainer}>
                            <div className={style.infoContainer}>
                                <span className={style.infoTitleText}>{t('detail.modal.review.title')}</span>
                                <div className={style.innerTextContainerDesc}>
                                    {t('detail.modal.review.desc1')}
                                    <br/>
                                    {t('detail.modal.review.desc2')}
                                </div>
                                <div className={style.starContainer}>
                                    {Array.from({length: 5}, (_, index) => (
                                        <img
                                            key={index}
                                            src={index < rating ? fullStar : emptyStar}
                                            alt={`${index + 1} star`}
                                            onClick={() => handleStarClick(index)}
                                            className={`${style.star} ${consultationData.reviewCount != null ? style.disabled : style.clickable}`}
                                        />
                                    ))}
                                    {reviewRateValidator === ValidateStatus.UNFILLED && (
                                        <p className={style.warningText}>{t('detail.modal.review.starRequired')}</p>
                                    )}
                                </div>
                            </div>
                            <div className={style.textareaInputContainer}>
                                <textarea
                                    readOnly={consultationData.reviewCount !== null}
                                    placeholder={t('detail.modal.review.placeholder')}
                                    className={style.textareaInput}
                                    value={review}
                                    onChange={(e) => {
                                        setReview(e.target.value)
                                        setReviewValidator(ValidateStatus.NONE)
                                    }}
                                />
                            </div>
                            {reviewValidator === ValidateStatus.BELOW_REQUIRED_LENGTH && (
                                <p className={style.warningText}>{t('detail.modal.review.lengthWarning')}</p>
                            )}
                        </div>
                        {consultationData?.review === null && consultationData.reviewCount === null && (
                            <button className={style.submitBtn} onClick={handleReviewSubmit}>
                                {t('btn.modal.review')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}